/**
 * create_template Tool - Create a reusable test template
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';
import type { CreateTemplateRequest } from '@runhuman/shared';

export interface CreateTemplateArgs extends CreateTemplateRequest {
  projectId: string;
}

export const createTemplateToolDefinition: Tool = {
  name: 'create_template',
  description: `Create a reusable test template.

Templates define default test configurations (instructions, URL, output schema, duration, device class, etc.) that can be reused across jobs and schedules.

After creating a template, use it with:
- run_template to create one-off jobs
- create_schedule to set up recurring automated runs`,
  inputSchema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'Project ID (required).',
      },
      name: {
        type: 'string',
        description: 'Template name (required). Must be unique within the project.',
      },
      testDescription: {
        type: 'string',
        description: 'Test instructions for the human tester.',
      },
      url: {
        type: 'string',
        description: 'Default URL to test.',
      },
      outputSchema: {
        type: 'object',
        description: 'JSON schema for structured result extraction.',
      },
      resultsTemplate: {
        type: 'string',
        description: 'MDForm template for free-form text reports (alternative to outputSchema).',
      },
      targetDurationMinutes: {
        type: 'number',
        description: 'Expected test duration in minutes (1-60).',
        minimum: 1,
        maximum: 60,
      },
      deviceClass: {
        type: 'string',
        enum: ['desktop', 'mobile', 'both'],
        description: 'Device class hint for the claim surface and viewport. Default: both.',
      },
      githubRepos: {
        type: 'array',
        items: { type: 'string' },
        description: 'GitHub repos for context (format: "owner/repo" each).',
      },
      githubRepo: {
        type: 'string',
        description: 'Single GitHub repo for context (format: "owner/repo"). Use githubRepos for multiple.',
      },
      autoCreateGithubIssues: {
        type: 'boolean',
        description: 'Auto-create GitHub issues from test findings.',
      },
      autoCreateOnlyTesterSurfaced: {
        type: 'boolean',
        description: 'When auto-creating issues, only file findings the tester explicitly surfaced (skip telemetry-only).',
      },
      reopenIssuesOnDuplicate: {
        type: 'boolean',
        description: 'When a duplicate match resolves to a closed issue, reopen it. Defaults to false: closed issues stay closed.',
      },
      commentOnDuplicate: {
        type: 'boolean',
        description: 'Default for jobs from this template: when a finding matches an existing issue, post a cross-reference comment on that issue. Defaults to false: re-runs do not comment on existing issues.',
      },
    },
    required: ['projectId', 'name'],
  },
};

export async function handleCreateTemplate(
  args: CreateTemplateArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const { projectId, ...templateData } = args;
    const template = await jobClient.createTemplate(projectId, templateData);

    return {
      content: [{
        type: 'text',
        text: `Template Created!

Name: ${template.name}
Template ID: ${template.id}
Project: ${template.projectId}${template.url ? `\nDefault URL: ${template.url}` : ''}${template.testDescription ? `\nInstructions: ${template.testDescription.substring(0, 100)}${template.testDescription.length > 100 ? '...' : ''}` : ''}${template.targetDurationMinutes ? `\nDuration: ${template.targetDurationMinutes} minutes` : ''}${template.deviceClass ? `\nDevice: ${template.deviceClass}` : ''}

Use this template:
- run_template(projectId: "${projectId}", template: "${template.name}")
- create_schedule(projectId: "${projectId}", templateId: "${template.id}", ...)`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to create template\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
